import { EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InfoCallout } from '@/components/ui/info-callout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useValidateIntegration } from '@/hooks/useValidateIntegration';

import { useDeleteMasterKey, useSetMasterKey } from './hooks/useMasterKey';
import type { InfraCredentialDialogSource } from './InfraCredentialConfigDialog';

/**
 * Structured config dialog for the Bright Data proxy credential. Bright Data
 * hands operators four separate values (Host / Port / Username / Password)
 * rather than a single URL, so this dialog collects them individually and
 * assembles the canonical `http://<username>:<password>@<host>:<port>` proxy URL
 * client-side, sending it as the `apiKey` of `PUT /enrichment/brightdata_proxy/key`.
 *
 * The enrichment key endpoint does NOT validate on set, so Save first validates
 * the assembled URL via `/integration/validate` (which runs the server-side
 * `brightdata_proxy` validator: a proxy-connectivity check) and only stores it
 * when validation passes. Any validation error surfaces through the inline error
 * callout below. The assembled URL/password is never logged or echoed.
 *
 * The GET response only exposes `configured` (never the stored value), so when a
 * proxy is already configured the fields start at their defaults/empty rather
 * than prefilled with the current credential.
 */

/** Bright Data's standard super-proxy endpoint — prefilled defaults. */
const DEFAULT_HOST = 'brd.superproxy.io';
const DEFAULT_PORT = '33335';

const TOS_WARNING =
  "For web scraping only. Do not use this proxy for password spraying or authenticated login attempts, which violates Bright Data's Acceptable Use Policy.";

interface BrightDataProxyConfigDialogProps {
  source: InfraCredentialDialogSource | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * The `@/utils/api` mutation wrapper packs the backend message into `Error.name`
 * (via `getQueryError` when `getErrorFromResponse` is set) and a support hint
 * into `Error.message`. Prefer the backend title so the real reason is shown
 * rather than swallowed.
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name && error.name !== 'Error') return error.name;
    if (error.message) return error.message;
  }
  return 'Failed to save credential. Please try again.';
}

export function BrightDataProxyConfigDialog({
  source,
  onOpenChange,
}: BrightDataProxyConfigDialogProps) {
  const [host, setHost] = useState(DEFAULT_HOST);
  const [port, setPort] = useState(DEFAULT_PORT);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [iconError, setIconError] = useState(false);

  const { mutate: setMasterKey, isPending: isSaving } = useSetMasterKey();
  const { mutate: deleteMasterKey, isPending: isDeleting } =
    useDeleteMasterKey();
  const { validateIntegration, isValidating } = useValidateIntegration();

  const isPending = isSaving || isDeleting || isValidating;
  const Icon = source?.Icon;
  // Host/Port are prefilled; only username + password gate Save.
  const canSave = username.trim() !== '' && password.trim() !== '';

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setHost(DEFAULT_HOST);
      setPort(DEFAULT_PORT);
      setUsername('');
      setPassword('');
      setVisible(false);
      setValidationError(null);
    }
    onOpenChange(open);
  };

  const handleSave = async () => {
    if (!source || !canSave) return;
    setValidationError(null);
    // Assemble the canonical proxy URL client-side; the validator (and the key
    // store) expect `api_key` to already be the proxy URL. Username/password
    // routinely contain reserved URL characters (@ # / % : ?), so percent-encode
    // the userinfo to match the backend's `BuildBrightDataProxyURL` (which uses
    // Go's `url.UserPassword` to encode userinfo), while leaving the prefilled
    // host and numeric port untouched.
    const apiKey = `http://${encodeURIComponent(username.trim())}:${encodeURIComponent(password.trim())}@${host.trim()}:${port.trim()}`;

    // The enrichment key store does not validate on set, so validate the
    // assembled proxy URL server-side first and only persist it when valid.
    const { isValid, validationErrors } = await validateIntegration({
      provider: source.id,
      config: { api_key: apiKey },
      suppressErrorToast: true,
    });
    if (!isValid) {
      const firstError = validationErrors?.[0];
      setValidationError(
        firstError
          ? `${firstError.title}: ${firstError.message}`
          : 'Validation failed. Please check your credentials.'
      );
      return;
    }

    setMasterKey(
      { plugin: source.id, apiKey },
      {
        onSuccess: () => handleOpenChange(false),
        onError: error => setValidationError(getErrorMessage(error)),
      }
    );
  };

  const handleClear = () => {
    if (!source) return;
    setValidationError(null);
    deleteMasterKey(
      { plugin: source.id },
      {
        onSuccess: () => handleOpenChange(false),
        onError: error => setValidationError(getErrorMessage(error)),
      }
    );
  };

  return (
    <Dialog open={!!source} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {source &&
              (iconError ? (
                Icon && <Icon className="text-chariot-text-secondary size-5" />
              ) : (
                <img
                  src={`/icons/${source.id}.png`}
                  alt=""
                  className="size-5"
                  onError={() => setIconError(true)}
                />
              ))}
            Configure {source?.label}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Acceptable-use / TOS warning */}
          <InfoCallout variant="warning">{TOS_WARNING}</InfoCallout>

          {/* Host + Port */}
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label
                htmlFor="brightdata-proxy-host"
                className="text-chariot-text-secondary text-xs font-medium"
              >
                Host
              </Label>
              <Input
                id="brightdata-proxy-host"
                type="text"
                autoComplete="off"
                value={host}
                onChange={event => {
                  setHost(event.target.value);
                  setValidationError(null);
                }}
              />
            </div>
            <div className="flex w-24 flex-col gap-1.5">
              <Label
                htmlFor="brightdata-proxy-port"
                className="text-chariot-text-secondary text-xs font-medium"
              >
                Port
              </Label>
              <Input
                id="brightdata-proxy-port"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={port}
                onChange={event => {
                  setPort(event.target.value);
                  setValidationError(null);
                }}
              />
            </div>
          </div>

          {/* Username (identifier — not masked) */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="brightdata-proxy-username"
              className="text-chariot-text-secondary text-xs font-medium"
            >
              Username<span className="text-error ml-0.5">*</span>
            </Label>
            <Input
              id="brightdata-proxy-username"
              type="text"
              autoComplete="off"
              placeholder="brd-customer-hl_...-zone-..."
              value={username}
              onChange={event => {
                setUsername(event.target.value);
                setValidationError(null);
              }}
            />
          </div>

          {/* Password (masked with show/hide toggle) */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="brightdata-proxy-password"
              className="text-chariot-text-secondary text-xs font-medium"
            >
              Password<span className="text-error ml-0.5">*</span>
            </Label>
            <div className="relative">
              <Input
                id="brightdata-proxy-password"
                type={visible ? 'text' : 'password'}
                autoComplete="off"
                placeholder={
                  source?.configured
                    ? 'Enter a new password to replace'
                    : 'Enter proxy password'
                }
                value={password}
                onChange={event => {
                  setPassword(event.target.value);
                  setValidationError(null);
                }}
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setVisible(prev => !prev)}
                className="text-chariot-text-tertiary hover:text-chariot-text-secondary absolute top-1/2 right-3 -translate-y-1/2"
                aria-label={visible ? 'Hide field' : 'Show field'}
              >
                {visible ? (
                  <EyeSlashIcon className="size-4" />
                ) : (
                  <EyeIcon className="size-4" />
                )}
              </button>
            </div>
          </div>

          {/* Backend validation error */}
          {validationError && (
            <InfoCallout variant="error" role="alert" aria-live="assertive">
              {validationError}
            </InfoCallout>
          )}

          {/* Current proxy status */}
          <div className="flex items-center gap-2">
            <KeyIcon className="text-chariot-text-tertiary size-4 shrink-0" />
            <span className="text-chariot-text-secondary text-xs">
              {source?.configured
                ? 'A proxy is configured'
                : 'No proxy configured'}
            </span>
          </div>
        </div>

        <DialogFooter className="flex-row items-center">
          {source?.configured && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={isPending}
              className="text-error hover:text-error mr-auto"
            >
              {isDeleting ? 'Removing...' : 'Clear Key'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isPending || !canSave}
          >
            {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
